import { BaseJsBridge } from './BaseJsBridge';
import { insertScript, getURLSearchParams } from '../utils';

let wx;

class WechatJsBridge extends BaseJsBridge {
  constructor() {
    super('wx');
  }

  /**
   * 初始化SDK
   * @param {Object} config 配置
   * @param {String} config.sdkUrl=https://res.wx.qq.com/open/js/jweixin-1.6.0.js 微信sdk url
   * @returns {Promise<void>}
   */
  async init(config) {
    if (this.initialized) {
      return Promise.reject(this.fail({ msg: 'SDK已初始化' }));
    }

    config = {
      sdkUrl: 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
      ...config,
    };
    try {
      await insertScript('WechatJsSdkScript', config.sdkUrl);
    } catch (e) {
      return Promise.reject(this.fail({ msg: '微信SDK加载失败' }));
    }

    wx = window.wx;

    if (!config.appId) {
      this.initialized = true;
      return;
    }

    // 遍历对象方法，拦截请求
    const ignoreList = ['constructor', 'init'];
    for (const propertyName of Object.getOwnPropertyNames(Object.getPrototypeOf(this))) {
      if (typeof this[propertyName] === 'function' && ignoreList.indexOf(propertyName) === -1) {
        this['_' + propertyName] = this[propertyName];
        this[propertyName] = (...args) => {
          if (!this.initialized) {
            return Promise.reject(this.fail({ msg: '微信SDK未初始化' }));
          } else {
            return this['_' + propertyName].apply(this, args);
          }
        };
      }
    }

    return new Promise((resolve, reject) => {
      wx.config({
        debug: false,
        ...config,
      });
      wx.ready(() => {
        // config信息验证后会执行ready方法，所有接口调用都必须在config接口获得结果之后，config是一个客户端的异步操作，所以如果需要在页面加载时就调用相关接口，则须把相关接口放在ready函数中调用来确保正确执行。对于用户触发时才调用的接口，则可以直接调用，不需要放在ready函数中。
        console.log('wx.ready:');
        this.initialized = true;
        resolve(
          this.success({
            msg: 'SDK初始化成功',
          })
        );
      });
      wx.error(err => {
        // config信息验证失败会执行error函数，如签名过期导致验证失败，具体错误信息可以打开config的debug模式查看，也可以在返回的res参数中查看，对于SPA可以在这里更新签名。
        console.log('wx.error:', err);
        this.initialized = false;
        reject(
          this.fail({
            msg: err.errMsg,
          })
        );
      });
    });
  }

  /**
   * 扫码和识别本地二维码
   * @returns {Promise<unknown>}
   */
  async scanQRCode() {
    return new Promise((resolve, reject) => {
      wx.scanQRCode({
        needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果，
        scanType: ['qrCode', 'barCode'], // 可以指定扫二维码还是一维码，默认二者都有
        success: res => {
          const result = res.resultStr; // 当needResult 为 1 时，扫码返回的结果
          resolve(
            this.success({
              msg: '扫码成功',
              data: { result },
            })
          );
        },
        fail: err => {
          reject(
            this.fail({
              msg: err.errMsg,
            })
          );
        },
      });
    });
  }

  /**
   * 选择本地相册图片或者视频
   * @returns {Promise<unknown>}
   */
  async openImageVideoPicker() {
    return new Promise((resolve, reject) => {
      wx.chooseImage({
        count: 1, // 默认9
        sizeType: ['original', 'compressed'], // 可以指定是原图还是压缩图，默认二者都有
        sourceType: ['album', 'camera'], // 可以指定来源是相册还是相机，默认二者都有
        success: res => {
          const localIds = res.localIds; // 返回选定照片的本地ID列表，localId可以作为img标签的src属性显示图片
          resolve(
            this.success({
              msg: '选择资源成功',
              data: { localIds },
            })
          );
        },
        fail: err => {
          reject(
            this.fail({
              msg: err.errMsg,
            })
          );
        },
      });
    });
  }

  /**
   * 跳转到原生指定页面
   * @param {Object} params 跳转参数
   * @returns {Promise<unknown>}
   */
  async navigateTo(params) {
    return new Promise((resolve, reject) => {
      wx.miniProgram.navigateTo({
        url: params.path,
        success: res => {
          console.log('跳转成功', res);
          resolve(
            this.success({
              msg: '跳转成功',
            })
          );
        },
        fail: err => {
          reject(
            this.fail({
              msg: err.errMsg,
            })
          );
        },
      });
    });
  }

  /**
   * 返回原生页面
   * @param {Object} params 返回参数
   * @returns {Promise<unknown>}
   */
  async navigateBack(params = {}) {
    return new Promise((resolve, reject) => {
      wx.miniProgram.navigateBack({
        delta: params.delta,
        success: res => {
          console.log('返回成功', res);
          resolve(
            this.success({
              msg: '返回成功',
            })
          );
        },
        fail: err => {
          reject(
            this.fail({
              msg: err.errMsg,
            })
          );
        },
      });
    });
  }

  /**
   * 获取子服务accessToken
   * @param {Object} getTokenParams - 请求参数信息
   *  - @param {String} getTokenParams.url - 请求接口地址
   *  - @param {Object} getTokenParams.params - 获取token的参数（除code、appId外的其他参数）
   *  - @param {String} getTokenParams.method - 请求方法 默认POST
   *  - @param {String} getTokenParams.headers - 请求头
   * @returns {Promise<unknown>}
   */
  async getServiceAccessToken(getTokenParams = {}) {
    if (!getTokenParams?.url) {
      return Promise.reject(this.fail({ msg: '获取accessToken异常，缺少参数url' }));
    }
    const { params = {} } = getTokenParams;
    const urlParams = getURLSearchParams(window.location.href);
    const { code, appId, channelId, sourceApp, phone } = urlParams;
    const requestParams = {
      code,
      //appId: appId || channelId, // 因为子服务的环境变量里面配置appId会使子服务多一个层级，所以环境变量中改用channelId字段
      loginForm: 'miniApp',
      phone,
      ...params,
    };
    if (appId ) {
      requestParams.appId = appId;
    }
    if (!requestParams.code)
      return Promise.reject(this.fail({ msg: '获取accessToken异常，缺少参数 code' }));
    return this.sdkAjax({ ...getTokenParams, params: requestParams });
  }

  /**
   * 获取子服务用户信息
   * @param {Object} getUserInfoParams - 请求参数信息
   *  - @param {String} getUserInfoParams.url - 请求接口地址
   *  - @param {Object} getUserInfoParams.params - 获取用户信息的参数
   *  - @param {String} getUserInfoParams.method - 请求方法 默认POST
   *  - @param {String} getUserInfoParams.headers - 请求头
   * @returns {Promise<unknown>}
   */
  async getServiceUserInfo(getUserInfoParams = {}) {
    if (!getUserInfoParams?.url) {
      return Promise.reject(this.fail({ msg: '获取用户信息异常，缺少参数url' }));
    }
    return this.sdkAjax({ ...getUserInfoParams });
  }
}

export { WechatJsBridge };
